/**
 * Route planning tab.
 * Waypoint placement, naming, and route configuration.
 */

import { useState } from 'react';
import { MapPin, Send, X, Loader2 } from 'lucide-react';
import { ROUTE_PLANNING_METHODS, ROUTE_METHODS, ROUTE_DIRECTIONS, ROUTE_TYPES } from '@vtak/shared';
import { useDrawingStore } from '@/stores/drawing-store';
import { ShapeConfig } from './shape-config';
import { sendShapeToTak } from './send-shape';

/** Route planning tab with waypoint management and send controls */
export function RouteTab(): React.JSX.Element {
  const { activeTool, drawingState, vertices, startTool, finishDrawing, cancelDrawing, setSending, setSent, reset, shapeConfig, updateConfig } = useDrawingStore();
  const [sendError, setSendError] = useState('');

  const isRouteActive = activeTool === 'route';
  const isDrawing = isRouteActive && drawingState === 'drawing';
  const isConfirming = isRouteActive && drawingState === 'confirming';
  const isSending = isRouteActive && drawingState === 'sending';
  const isSent = isRouteActive && drawingState === 'sent';

  async function handleSend(): Promise<void> {
    setSending();
    setSendError('');
    const result = await sendShapeToTak('route', vertices, 0, shapeConfig);
    setSent(result.ok);
    if (!result.ok) setSendError(result.error);
    if (result.ok) setTimeout(reset, 1500);
  }

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      {!isRouteActive && (
        <button
          onClick={() => startTool('route')}
          className="flex items-center justify-center gap-2 rounded bg-echo-yellow px-3 py-2.5 text-sm font-medium text-black"
        >
          <MapPin size={16} /> Start Route
        </button>
      )}

      {isDrawing && (
        <div className="space-y-3 rounded bg-white/[0.04] p-3">
          <p className="text-xs text-muted-foreground">
            Click map to add waypoints. {vertices.length} placed.
          </p>
          <div className="space-y-1">
            {vertices.map((_, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-foreground">
                <span className="w-8 text-muted-foreground">{resolveLabel(i, vertices.length)}</span>
                <span>{vertices[i]!.lat.toFixed(5)}, {vertices[i]!.lon.toFixed(5)}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            {vertices.length >= 2 && (
              <button
                onClick={finishDrawing}
                className="flex items-center gap-1 rounded bg-echo-yellow px-3 py-1.5 text-xs font-medium text-black"
              >
                Finish Route
              </button>
            )}
            <button onClick={cancelDrawing} className="flex items-center gap-1 rounded bg-white/[0.06] px-3 py-1.5 text-xs text-red-400">
              <X size={12} /> Cancel
            </button>
          </div>
        </div>
      )}

      {isConfirming && (
        <div className="space-y-3">
          <ShapeConfig />
          <SelectField label="Planning Method" value={shapeConfig.planningMethod} options={ROUTE_PLANNING_METHODS} onChange={(v) => updateConfig({ planningMethod: v })} />
          <SelectField label="Route Method" value={shapeConfig.routeMethod} options={ROUTE_METHODS} onChange={(v) => updateConfig({ routeMethod: v })} />
          <SelectField label="Direction" value={shapeConfig.direction} options={ROUTE_DIRECTIONS} onChange={(v) => updateConfig({ direction: v })} />
          <SelectField label="Route Type" value={shapeConfig.routeType} options={ROUTE_TYPES} onChange={(v) => updateConfig({ routeType: v })} />
          <div className="flex gap-2">
            <button onClick={handleSend} className="flex flex-1 items-center justify-center gap-1 rounded bg-echo-yellow px-3 py-2 text-xs font-medium text-black">
              <Send size={12} /> Send to TAK
            </button>
            <button onClick={cancelDrawing} className="rounded bg-white/[0.06] px-3 py-2 text-xs text-red-400">Cancel</button>
          </div>
          {sendError && <p className="text-xs text-red-400">{sendError}</p>}
        </div>
      )}

      {isSending && <p className="text-xs text-muted-foreground"><Loader2 size={14} className="inline animate-spin" /> Sending...</p>}
      {isSent && <p className="text-xs text-green-400">Route sent successfully.</p>}
    </div>
  );
}

/** Reusable select field for route options */
function SelectField(props: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <div>
      <label className="text-xs text-muted-foreground">{props.label}</label>
      <select
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="mt-1 w-full rounded bg-white/[0.06] px-3 py-1.5 text-sm text-foreground outline-none"
      >
        {props.options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

/** Resolve waypoint label based on index */
function resolveLabel(index: number, total: number): string {
  if (index === 0) return 'SP';
  if (index === total - 1 && total > 1) return 'VDO';
  return `CP${index}`;
}
